import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import socketService from '../services/socketService';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications debe ser usado dentro de un NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastNotifications, setToastNotifications] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { user } = useAuth();

  // Cargar notificaciones iniciales solo si el usuario está autenticado
  useEffect(() => {
    if (user) {
      loadNotifications();
      loadUnreadCount();
      
      // Conectar WebSocket
      const token = localStorage.getItem('token');
      if (token) {
        socketService.connect(token);
        
        // Escuchar notificaciones en tiempo real
        socketService.on('notification', handleRealtimeNotification);
      }
    } else {
      // Limpiar notificaciones si no hay usuario
      setNotifications([]);
      setUnreadCount(0);
      
      // Desconectar WebSocket
      socketService.disconnect();
    }

    // Cleanup
    return () => {
      socketService.off('notification', handleRealtimeNotification);
    };
  }, [user]);

  // Manejar notificaciones en tiempo real
  const handleRealtimeNotification = (notification) => {
    console.log('📨 Nueva notificación en tiempo real:', notification);
    
    // Agregar a la lista de notificaciones
    setNotifications(prev => [notification, ...prev]);
    
    // Incrementar contador de no leídas
    setUnreadCount(prev => prev + 1);
    
    // Mostrar toast de notificación
    addToastNotification(notification);
  };

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notificaciones');
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
      setNotifications([]);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await api.get('/notificaciones/no-leidas');
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Error cargando contador de no leídas:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notificaciones/${notificationId}/leer`);
      setNotifications(prev => 
        Array.isArray(prev) ? prev.map(n => 
          n.id === notificationId ? { ...n, leida: true } : n
        ) : []
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marcando como leída:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/notificaciones/leer-todas');
      setNotifications(prev => Array.isArray(prev) ? prev.map(n => ({ ...n, leida: true })) : []);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notificaciones/${notificationId}`);
      setNotifications(prev => Array.isArray(prev) ? prev.filter(n => n.id !== notificationId) : []);
      const deletedNotification = Array.isArray(notifications) ? notifications.find(n => n.id === notificationId) : null;
      if (deletedNotification && !deletedNotification.leida) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error eliminando notificación:', error);
    }
  };

  const addToastNotification = (notification) => {
    const toastId = Date.now();
    const newToast = {
      ...notification,
      id: toastId
    };
    setToastNotifications(prev => [...prev, newToast]);

    // Auto-remover después de 6 segundos
    setTimeout(() => {
      removeToastNotification(toastId);
    }, 6000);
  };

  const removeToastNotification = (toastId) => {
    setToastNotifications(prev => prev.filter(toast => toast.id !== toastId));
  };

  const markToastAsRead = async (notificationId) => {
    await markAsRead(notificationId);
    // También actualizar en las notificaciones del panel
    setNotifications(prev => 
      Array.isArray(prev) ? prev.map(n => 
        n.id === notificationId ? { ...n, leida: true } : n
      ) : []
    );
  };

  const openPanel = () => {
    setIsPanelOpen(true);
  };

  const closePanel = () => {
    setIsPanelOpen(false);
  };

  const refreshNotifications = () => {
    loadNotifications();
    loadUnreadCount();
  };

  const value = {
    notifications,
    unreadCount,
    toastNotifications,
    isPanelOpen,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addToastNotification,
    removeToastNotification,
    markToastAsRead,
    openPanel,
    closePanel,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
